import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type VideoApiCapabilitiesResponse, type VideoApiDocMode } from '../services/api';
import toast from '../components/Toast';
import {
  AlertCircle,
  BookOpen,
  Check,
  ChevronDown,
  ChevronUp,
  Clapperboard,
  Copy,
  ExternalLink,
  Key,
  RefreshCw,
  Sparkles,
  Terminal,
  Video,
} from 'lucide-react';
import { useSiteName } from '../platform/useSiteName';

type VideoCapabilityMode = NonNullable<VideoApiCapabilitiesResponse['models'][number]['modes'][VideoApiDocMode]>;

type UserInfo = {
  id: string;
  email: string;
  name: string | null;
  apiKey: string;
  credits: number;
  role?: string;
};

function useSiteUrl() {
  const [siteUrl, setSiteUrl] = useState('');

  useEffect(() => {
    api.getSiteUrl().then((res) => {
      setSiteUrl(res.siteUrl || window.location.origin);
    }).catch(() => {
      setSiteUrl(window.location.origin);
    });
  }, []);

  return siteUrl;
}

function getVideoExamples(baseUrl: string, modelId: string) {
  return {
    javascript: {
      capabilities: `const apiKey = "YOUR_API_KEY";

const capabilities = await fetch("${baseUrl}/api/v1/videos/capabilities", {
  headers: {
    Authorization: \`Bearer \${apiKey}\`
  }
}).then((res) => res.json());

console.log(capabilities.data.models);`,
      preview: `const preview = await fetch("${baseUrl}/api/v1/videos/credits/preview", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: "Bearer YOUR_API_KEY"
  },
  body: JSON.stringify({
    modelId: "${modelId}",
    mode: "img2video",
    duration: 5,
    resolution: "720p",
    n: 1
  })
}).then((res) => res.json());

console.log(preview.data);`,
      generate: `const task = await fetch("${baseUrl}/api/v1/videos/generations", {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Authorization: "Bearer YOUR_API_KEY"
  },
  body: JSON.stringify({
    modelId: "${modelId}",
    mode: "img2video",
    prompt: "a cinematic rainy street, dramatic lighting",
    duration: 5,
    resolution: "720p",
    aspectRatio: "16:9",
    n: 1,
    referenceImages: [
      "https://example.com/ref1.jpg"
    ],
    audioReferenceUrls: [],
    videoReferenceUrls: []
  })
}).then((res) => res.json());

console.log(task.data.jobIds);`,
      getJob: `const job = await fetch("${baseUrl}/api/v1/videos/job_xxx", {
  headers: {
    Authorization: "Bearer YOUR_API_KEY"
  }
}).then((res) => res.json());

console.log(job.data.status, job.data.resultUrl);`,
      listJobs: `const jobs = await fetch("${baseUrl}/api/v1/videos?page=1&pageSize=20&status=processing", {
  headers: {
    Authorization: "Bearer YOUR_API_KEY"
  }
}).then((res) => res.json());

console.log(jobs.data);`,
    },
    python: {
      capabilities: `import requests

headers = {
    "Authorization": "Bearer YOUR_API_KEY"
}

resp = requests.get("${baseUrl}/api/v1/videos/capabilities", headers=headers, timeout=60)
data = resp.json()
print(data["data"]["models"])`,
      preview: `import requests

headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}

payload = {
    "modelId": "${modelId}",
    "mode": "img2video",
    "duration": 5,
    "resolution": "720p",
    "n": 1
}

resp = requests.post("${baseUrl}/api/v1/videos/credits/preview", headers=headers, json=payload, timeout=60)
print(resp.json()["data"])`,
      generate: `import requests

headers = {
    "Authorization": "Bearer YOUR_API_KEY",
    "Content-Type": "application/json"
}

payload = {
    "modelId": "${modelId}",
    "mode": "img2video",
    "prompt": "a cinematic rainy street, dramatic lighting",
    "duration": 5,
    "resolution": "720p",
    "aspectRatio": "16:9",
    "n": 1,
    "referenceImages": [
        "https://example.com/ref1.jpg"
    ],
    "audioReferenceUrls": [],
    "videoReferenceUrls": []
}

resp = requests.post("${baseUrl}/api/v1/videos/generations", headers=headers, json=payload, timeout=120)
print(resp.json()["data"]["jobIds"])`,
      getJob: `import requests

headers = {
    "Authorization": "Bearer YOUR_API_KEY"
}

resp = requests.get("${baseUrl}/api/v1/videos/job_xxx", headers=headers, timeout=60)
print(resp.json()["data"])`,
      listJobs: `import requests

headers = {
    "Authorization": "Bearer YOUR_API_KEY"
}

resp = requests.get("${baseUrl}/api/v1/videos?page=1&pageSize=20&status=processing", headers=headers, timeout=60)
print(resp.json()["data"])`,
    },
    curl: {
      capabilities: `curl -X GET ${baseUrl}/api/v1/videos/capabilities \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
      preview: `curl -X POST ${baseUrl}/api/v1/videos/credits/preview \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "modelId": "${modelId}",
    "mode": "img2video",
    "duration": 5,
    "resolution": "720p",
    "n": 1
  }'`,
      generate: `curl -X POST ${baseUrl}/api/v1/videos/generations \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "modelId": "${modelId}",
    "mode": "img2video",
    "prompt": "a cinematic rainy street, dramatic lighting",
    "duration": 5,
    "resolution": "720p",
    "aspectRatio": "16:9",
    "n": 1,
    "referenceImages": [
      "https://example.com/ref1.jpg"
    ],
    "audioReferenceUrls": [],
    "videoReferenceUrls": []
  }'`,
      getJob: `curl -X GET ${baseUrl}/api/v1/videos/job_xxx \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
      listJobs: `curl -X GET "${baseUrl}/api/v1/videos?page=1&pageSize=20&status=processing" \\
  -H "Authorization: Bearer YOUR_API_KEY"`,
    },
  };
}

function formatDurationSpec(spec: VideoCapabilityMode['supportedDurations']) {
  if (!spec) return '-';
  if (spec.type === 'enum') return spec.values.join(' / ');
  return `${spec.min}-${spec.max} 秒，步长 ${spec.step}`;
}

function modeLabel(mode: VideoApiDocMode) {
  if (mode === 'text2video') return '文生视频';
  if (mode === 'img2video') return '图生视频';
  return '首尾帧视频';
}

export default function VideoApiDocsPage() {
  const navigate = useNavigate();
  const siteName = useSiteName('AI 视频生成');
  const siteUrl = useSiteUrl();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingCapabilities, setLoadingCapabilities] = useState(true);
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<VideoApiCapabilitiesResponse | null>(null);
  const [activeLang, setActiveLang] = useState<'javascript' | 'python' | 'curl'>('javascript');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['auth', 'capabilities', 'generate', 'jobs'])
  );

  useEffect(() => {
    const savedUser = api.getSavedUser();
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(savedUser);
  }, [navigate]);

  useEffect(() => {
    if (!user?.apiKey) return;
    setLoadingCapabilities(true);
    setCapabilityError(null);
    api.getVideoApiCapabilities(user.apiKey)
      .then((data) => setCapabilities(data))
      .catch((error) => {
        setCapabilityError(error instanceof Error ? error.message : '获取视频能力失败');
      })
      .finally(() => setLoadingCapabilities(false));
  }, [user?.apiKey]);

  const exampleModel = capabilities?.models.find((item) => Object.keys(item.modes || {}).length > 0) || capabilities?.models[0];
  const exampleModelId = exampleModel?.modelId || 'doubao-seedance-2.0';
  const examples = useMemo(
    () => getVideoExamples(siteUrl || window.location.origin, exampleModelId),
    [siteUrl, exampleModelId]
  );

  const handleRefreshApiKey = async () => {
    if (!confirm('确定要重新生成 API Key 吗？旧的 API Key 将立即失效。')) {
      return;
    }

    setRefreshing(true);
    try {
      const data = await api.refreshApiKey();
      setUser((prev) => prev ? { ...prev, apiKey: data.apiKey } : null);
      toast.success('API Key 已重新生成');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '刷新失败');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const SectionHeader = ({ id, title, icon: Icon }: { id: string; title: string; icon: React.ElementType }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {expandedSections.has(id) ? (
        <ChevronUp className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  const CodeBlock = ({ code, language }: { code: string; language: string }) => (
    <div className="relative group">
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => handleCopy(code)}
          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-xl overflow-x-auto text-sm leading-relaxed">
        <code>{code}</code>
      </pre>
      <div className="absolute left-2 top-2 px-2 py-1 bg-gray-800 rounded text-xs text-gray-400">
        {language}
      </div>
    </div>
  );

  const ParamTable = ({ params }: { params: { name: string; type: string; required: string; default?: string; description: string }[] }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-700">参数</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">类型</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">必填</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">默认值</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">说明</th>
          </tr>
        </thead>
        <tbody>
          {params.map((param, idx) => (
            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4 font-mono text-violet-600">{param.name}</td>
              <td className="py-3 px-4 text-gray-600">{param.type}</td>
              <td className="py-3 px-4">
                <span className={`px-2 py-1 rounded-full text-xs ${param.required === '是' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                  {param.required}
                </span>
              </td>
              <td className="py-3 px-4 text-gray-500">{param.default || '-'}</td>
              <td className="py-3 px-4 text-gray-600">{param.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-light-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="flex items-center gap-2 text-light-text hover:text-accent-blue transition-colors">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{siteName}</span>
              </button>
              <span className="text-light-muted">/</span>
              <span className="font-medium text-light-text">视频 API 文档</span>
            </div>
            <button
              onClick={() => navigate('/api-docs')}
              className="inline-flex items-center gap-2 rounded-xl border border-light-border px-4 py-2 text-sm text-light-text hover:bg-light-hover transition-colors"
            >
              <Clapperboard className="w-4 h-4" />
              返回图片 API 文档
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className="inline-flex items-center rounded-full bg-fuchsia-100 px-3 py-1 text-sm font-medium text-fuchsia-700">
              仅支持公网 URL
            </span>
            <span className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1 text-sm font-medium text-violet-700">
              异步任务 API
            </span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">视频生成 API</h1>
          <p className="text-gray-600">
            通过独立的视频 API 发起任务、查询能力、预估积分并轮询结果。当前版本不支持素材上传，所有参考素材都需要公网 URL。
          </p>
        </div>

        <div className="bg-gradient-to-r from-fuchsia-600 to-violet-600 rounded-2xl p-6 mb-8 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6" />
              <h2 className="text-xl font-semibold">您的 API Key</h2>
            </div>
            <button
              onClick={handleRefreshApiKey}
              disabled={refreshing}
              className="p-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm">{refreshing ? '刷新中...' : '重新生成'}</span>
            </button>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between">
            <code className="font-mono text-sm break-all">{user.apiKey}</code>
            <button
              onClick={() => handleCopy(user.apiKey)}
              className="ml-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? '已复制' : '复制'}</span>
            </button>
          </div>
          <p className="mt-4 text-sm text-white/80">
            视频接口使用的是开发者 API Key，而不是站内登录 Token。当前剩余积分: {user.credits}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-violet-600" />
              快速开始
            </h2>
            <div className="flex gap-2">
              {(['javascript', 'python', 'curl'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setActiveLang(lang)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeLang === lang
                      ? 'bg-violet-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {lang === 'javascript' ? 'JavaScript' : lang === 'python' ? 'Python' : 'cURL'}
                </button>
              ))}
            </div>
          </div>
          <CodeBlock code={examples[activeLang].generate.replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-900 mb-1">1. 先查能力</p>
              <p className="text-sm text-gray-600">先请求 `/api/v1/videos/capabilities`，再根据返回的模型和模式构造请求。</p>
            </div>
            <div className="rounded-xl bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-900 mb-1">2. 再预估积分</p>
              <p className="text-sm text-gray-600">调用 `/api/v1/videos/credits/preview` 预估本次消耗，避免积分不足。</p>
            </div>
            <div className="rounded-xl bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-900 mb-1">3. 最后轮询结果</p>
              <p className="text-sm text-gray-600">视频生成是异步任务，创建后通过任务详情或任务列表轮询状态。</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-violet-600" />
            当前视频能力
          </h2>
          {loadingCapabilities && (
            <div className="rounded-xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
              视频能力加载中...
            </div>
          )}
          {!loadingCapabilities && capabilityError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-600">
              获取视频能力失败：{capabilityError}
            </div>
          )}
          {!loadingCapabilities && !capabilityError && capabilities && (
            <div className="space-y-4">
              <div className="rounded-xl bg-violet-50 p-4 text-sm text-violet-700">
                鉴权方式：`{capabilities.auth.type}`，上传策略：`仅公网 URL`，不支持上传文件、`base64` 和 `storage key`
              </div>
              {capabilities.models.map((model) => (
                <div key={model.modelId} className="rounded-xl border border-gray-200 p-4">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <h3 className="text-base font-semibold text-gray-900">{model.name}</h3>
                    <code className="rounded bg-gray-100 px-2 py-1 text-xs text-violet-700">{model.modelId}</code>
                  </div>
                  {model.description && (
                    <p className="text-sm text-gray-600 mb-3">{model.description}</p>
                  )}
                  <div className="grid gap-3 md:grid-cols-3">
                    {(['text2video', 'img2video', 'first_last_frame'] as const).map((mode) => {
                      const info = model.modes[mode];
                      return (
                        <div key={mode} className="rounded-lg bg-gray-50 p-3">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900">{modeLabel(mode)}</span>
                            <span className={`rounded-full px-2 py-1 text-xs ${info?.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                              {info?.enabled ? '支持' : '不支持'}
                            </span>
                          </div>
                          {info ? (
                            <div className="space-y-1 text-xs text-gray-600">
                              <p>时长：{formatDurationSpec(info.supportedDurations)}</p>
                              <p>分辨率：{info.supportedResolutions.join(' / ') || '-'}</p>
                              <p>宽高比：{info.supportedAspectRatios.join(' / ') || '-'}</p>
                              <p>参考图上限：{info.maxReferenceImageCount}</p>
                              <p>参考音频上限：{info.maxAudioReferenceCount}</p>
                              <p>参考视频上限：{info.maxVideoReferenceCount}</p>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">当前模型未开放该模式</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4 mb-6">
          <SectionHeader id="auth" title="认证方式" icon={Key} />
          {expandedSections.has('auth') && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <p className="text-gray-600 mb-4">
                所有视频 API 请求都需要在 HTTP Header 中携带您的开发者 API Key：
              </p>
              <CodeBlock code={`Authorization: Bearer ${user.apiKey}`} language="http" />
            </div>
          )}
        </div>

        <div className="space-y-4 mb-6">
          <SectionHeader id="capabilities" title="查询能力 /videos/capabilities" icon={Video} />
          {expandedSections.has('capabilities') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <ParamTable
                params={[
                  { name: 'Authorization', type: 'string', required: '是', description: 'Bearer YOUR_API_KEY' },
                ]}
              />
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">请求示例</h4>
                <CodeBlock code={examples[activeLang].capabilities.replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">响应示例</h4>
                <CodeBlock
                  code={`{
  "ok": true,
  "data": {
    "auth": { "type": "bearer_api_key" },
    "inputPolicy": {
      "uploadSupported": false,
      "publicUrlOnly": true,
      "base64Supported": false,
      "storageKeySupported": false
    },
    "models": [
      {
        "modelId": "${exampleModelId}",
        "name": "${exampleModel?.name || 'Seedance 2.0'}",
        "description": "${exampleModel?.description || '视频生成模型'}",
        "defaults": {
          "duration": ${exampleModel?.defaults.duration || 5},
          "resolution": "${exampleModel?.defaults.resolution || '720p'}",
          "aspectRatio": "${exampleModel?.defaults.aspectRatio || '16:9'}"
        },
        "modes": {
          "img2video": {
            "enabled": true,
            "capabilities": {
              "audio": true,
              "audioReference": false,
              "videoReference": false,
              "cameraMovement": true,
              "motionIntensity": true
            },
            "supportedDurations": { "type": "enum", "values": [5, 10] },
            "supportedResolutions": ["720p", "1080p"],
            "supportedAspectRatios": ["16:9", "9:16"],
            "maxReferenceImageCount": 9,
            "allowedReferenceImageCounts": [1, 2, 3],
            "maxAudioReferenceCount": 0,
            "maxVideoReferenceCount": 0,
            "requiresPublicReferenceUrl": true,
            "inputPolicy": {
              "publicUrlOnly": true,
              "uploadSupported": false,
              "base64Supported": false,
              "storageKeySupported": false
            }
          }
        }
      }
    ]
  }
}`}
                  language="json"
                />
              </div>
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-900 mb-2">字段说明：</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><code className="font-mono text-violet-600">capabilities</code> — 该模式支持的高级能力开关（audio / audioReference / videoReference / cameraMovement / motionIntensity）</li>
                  <li><code className="font-mono text-violet-600">supportedDurations</code> — <code>{"{type:'enum',values:[]}"}</code> 枚举 或 <code>{"{type:'range',min,max,step}"}</code> 范围</li>
                  <li><code className="font-mono text-violet-600">allowedReferenceImageCounts</code> — 允许的参考图张数枚举（部分模型仅支持特定张数）</li>
                  <li><code className="font-mono text-violet-600">requiresPublicReferenceUrl</code> — 参考素材是否必须公网 URL</li>
                  <li>参考音频/视频时长上下限字段（如 <code className="font-mono">audioReferenceMaxDurationSec</code>）仅在对应能力开启时返回</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mb-6">
          <SectionHeader id="preview" title="预估积分 /videos/credits/preview" icon={BookOpen} />
          {expandedSections.has('preview') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <ParamTable
                params={[
                  { name: 'modelId', type: 'string', required: '是', default: exampleModelId, description: '视频模型 ID' },
                  { name: 'mode', type: 'string', required: '是', default: 'img2video', description: 'text2video / img2video / first_last_frame' },
                  { name: 'duration', type: 'integer', required: '是', default: '5', description: '视频时长，必须在当前模型支持范围内' },
                  { name: 'resolution', type: 'string', required: '是', default: '720p', description: '输出分辨率' },
                  { name: 'n', type: 'integer', required: '否', default: '1', description: '一次提交任务数量，1-100' },
                  { name: 'enableAudio', type: 'boolean', required: '否', default: 'false', description: '是否生成音频，需模型支持' },
                  { name: 'refVideoDurationSec', type: 'number', required: '否', default: '0', description: '参考视频时长合计（秒），用于输入侧计费' },
                  { name: 'refImageCount', type: 'integer', required: '否', default: '0', description: '参考图张数（含首尾帧），用于输入侧计费' },
                ]}
              />
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">请求示例</h4>
                <CodeBlock code={examples[activeLang].preview.replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">响应示例</h4>
                <CodeBlock
                  code={`{
  "ok": true,
  "data": {
    "creditsPerJob": 120,
    "creditsTotal": 120,
    "breakdown": {
      "output": 100,
      "refVideo": 15,
      "refImage": 5,
      "total": 120
    }
  }
}`}
                  language="json"
                />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mb-6">
          <SectionHeader id="generate" title="创建任务 /videos/generations" icon={Clapperboard} />
          {expandedSections.has('generate') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
                只支持公网 URL。`referenceImages`、`firstFrameImage`、`lastFrameImage`、`audioReferenceUrls`、`videoReferenceUrls`
                都必须是服务端可访问的公网地址。
              </div>
              <ParamTable
                params={[
                  { name: 'modelId', type: 'string', required: '是', default: exampleModelId, description: '视频模型 ID' },
                  { name: 'mode', type: 'string', required: '是', default: 'img2video', description: 'text2video / img2video / first_last_frame' },
                  { name: 'prompt', type: 'string', required: '是', description: '视频提示词，最多 4000 字（部分模型更严）' },
                  { name: 'duration', type: 'integer', required: '是', default: '5', description: '视频时长' },
                  { name: 'resolution', type: 'string', required: '是', default: '720p', description: '输出分辨率' },
                  { name: 'aspectRatio', type: 'string', required: '是', default: '16:9', description: '视频宽高比' },
                  { name: 'n', type: 'integer', required: '否', default: '1', description: '一次提交任务数量' },
                  { name: 'cameraMovement', type: 'string', required: '否', description: '镜头运动: static/pan_left/pan_right/zoom_in/zoom_out/tilt_up/tilt_down' },
                  { name: 'motionIntensity', type: 'string', required: '否', description: '运动幅度: low/medium/high' },
                  { name: 'enableAudio', type: 'boolean', required: '否', default: 'false', description: '是否生成音频，需模型支持' },
                  { name: 'referenceImages', type: 'string[]', required: '否', description: '图生视频参考图 URL 列表' },
                  { name: 'firstFrameImage', type: 'string', required: '否', description: '首尾帧模式的首帧 URL' },
                  { name: 'lastFrameImage', type: 'string', required: '否', description: '首尾帧模式的尾帧 URL' },
                  { name: 'audioReferenceUrls', type: 'string[]', required: '否', description: '参考音频 URL 列表' },
                  { name: 'videoReferenceUrls', type: 'string[]', required: '否', description: '参考视频 URL 列表' },
                  { name: 'videoReferenceDurations', type: 'number[]', required: '否', description: '参考视频时长列表（秒），与 videoReferenceUrls 按下标对齐，用于计费' },
                ]}
              />
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">请求示例</h4>
                <CodeBlock code={examples[activeLang].generate.replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">响应示例（201 Created）</h4>
                <CodeBlock
                  code={`{
  "ok": true,
  "data": {
    "jobIds": ["job_xxx"],
    "creditsUsed": 120,
    "creditsPerJob": 120
  }
}`}
                  language="json"
                />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mb-6">
          <SectionHeader id="jobs" title="查询任务 /videos 与 /videos/:id" icon={Terminal} />
          {expandedSections.has('jobs') && (
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">查询单任务</h4>
                  <CodeBlock code={examples[activeLang].getJob.replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">查询任务列表</h4>
                  <CodeBlock code={examples[activeLang].listJobs.replace('YOUR_API_KEY', user.apiKey)} language={activeLang} />
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">单任务响应示例</h4>
                <CodeBlock
                  code={`{
  "ok": true,
  "data": {
    "id": "job_xxx",
    "status": "processing",
    "model": "${exampleModelId}",
    "mode": "img2video",
    "prompt": "a cinematic rainy street, dramatic lighting",
    "duration": 5,
    "resolution": "720p",
    "aspectRatio": "16:9",
    "cameraMovement": "zoom_in",
    "motionIntensity": "medium",
    "enableAudio": false,
    "creditsUsed": 120,
    "resultDuration": null,
    "resultWidth": null,
    "resultHeight": null,
    "resultFileSize": null,
    "resultMimeType": null,
    "resultUrl": null,
    "coverUrl": null,
    "referenceImages": ["https://example.com/ref1.jpg"],
    "firstFrameImage": null,
    "lastFrameImage": null,
    "createdAt": "2026-06-15T10:00:00.000Z",
    "updatedAt": "2026-06-15T10:00:05.000Z",
    "completedAt": null,
    "error": null
  }
}`}
                  language="json"
                />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">任务列表响应示例</h4>
                <CodeBlock
                  code={`{
  "ok": true,
  "data": [ /* 单任务对象数组，结构同上 */ ],
  "pagination": {
    "page": 1,
    "pageSize": 20,
    "total": 35,
    "totalPages": 2
  }
}`}
                  language="json"
                />
              </div>
              <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">
                `resultUrl` 和 `coverUrl` 是当前结果的可访问地址，建议在任务完成后 **3 日内下载保存到本地**。
                若超过时效未保存，这两个地址后续可能失效。
              </div>
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-900 mb-2">任务状态与 error 字段：</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><code className="font-mono text-violet-600">status</code> — pending / submitting / processing / completed / failed / timeout</li>
                  <li>任务失败或超时时，<code className="font-mono text-violet-600">error</code> 返回 <code>{"{ code: 'generation_failed', message }"}</code>；正常时为 <code>null</code></li>
                  <li>列表分页：<code className="font-mono">pageSize</code> 默认 20、上限 50；隐藏任务不会出现在列表中</li>
                  <li><code className="font-mono text-violet-600">result*</code> 系列字段在任务完成后填充（时长/宽高/文件大小/MIME）</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4 mb-8">
          <SectionHeader id="errors" title="错误处理" icon={AlertCircle} />
          {expandedSections.has('errors') && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <p className="text-gray-600 mb-4">
                视频 API 的错误返回会裁剪内部 provider 细节，对外只暴露安全错误信息：
              </p>
              <CodeBlock
                code={`{
  "error": {
    "message": "无效的素材 URL",
    "type": "invalid_request_error",
    "code": "invalid_reference_url"
  }
}`}
                language="json"
              />
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">HTTP</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">type</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">code</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { http: '401', type: 'authentication_error', code: 'invalid_api_key', desc: 'API Key 无效或缺失' },
                      { http: '400', type: 'invalid_request_error', code: 'invalid_request', desc: '请求参数错误（必填缺失/非法值/时长分辨率不支持等）' },
                      { http: '400', type: 'invalid_request_error', code: 'invalid_reference_url', desc: '素材 URL 不合法或非公网地址' },
                      { http: '400', type: 'invalid_request_error', code: 'model_not_found', desc: '视频模型不存在' },
                      { http: '400', type: 'invalid_request_error', code: 'model_disabled', desc: '视频模型已禁用' },
                      { http: '404', type: 'invalid_request_error', code: 'invalid_request', desc: '任务不存在或不属于当前用户' },
                      { http: '429', type: 'invalid_request_error', code: 'insufficient_quota', desc: '积分不足' },
                      { http: '429', type: 'invalid_request_error', code: 'too_many_concurrent_jobs', desc: '并发任务数超限' },
                      { http: '400', type: 'invalid_request_error', code: 'too_many_jobs_per_submit', desc: '单次提交数量超限' },
                      { http: '500', type: 'api_error', code: 'internal_error', desc: '服务内部错误' },
                      { http: '500', type: 'api_error', code: 'missing_channel', desc: '模型未配置 API 渠道' },
                      { http: '—', type: '任务级', code: 'generation_failed', desc: '任务失败或超时，在任务详情的 error 字段返回' },
                    ].map((item) => (
                      <tr key={item.http + item.code} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4"><span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-mono">{item.http}</span></td>
                        <td className="py-3 px-4 font-mono text-gray-600 text-xs">{item.type}</td>
                        <td className="py-3 px-4 font-mono text-violet-600 text-xs">{item.code}</td>
                        <td className="py-3 px-4 text-gray-600">{item.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
          <a href="/faq" className="flex items-center gap-1 hover:text-violet-600 transition-colors">
            <BookOpen className="w-4 h-4" />
            常见问题
          </a>
          <a href="/" className="flex items-center gap-1 hover:text-violet-600 transition-colors">
            <ExternalLink className="w-4 h-4" />
            返回首页
          </a>
        </div>
      </main>
    </div>
  );
}
